import { useState, useRef, ChangeEvent } from 'react';
import { toast } from 'react-toastify';
import { getApiUrl, getApiHeaders } from '../utils/api';
import './AlbaranScanPage.css';

interface AlbaranItem {
    code: string;
    name: string;
    quantity: number;
    unit: string;
    unitCost: number | null;
    totalCost: number | null;
    currency: string;
}

interface AlbaranData {
    documentType: string;
    documentNumber: string;
    date: string;
    supplier: {
        name: string;
        contact: string;
    };
    items: AlbaranItem[];
    totals: {
        subtotal: number;
        tax: number;
        total: number;
        currency: string;
    };
    confidence: number;
}

function AlbaranScanPage() {
    const [image, setImage] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [albaranData, setAlbaranData] = useState<AlbaranData | null>(null);
    const [editableItems, setEditableItems] = useState<AlbaranItem[]>([]);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const cameraInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const preview = URL.createObjectURL(file);
        setImage(preview);
        processAlbaran(file);
    };

    const processAlbaran = async (file: File) => {
        setIsProcessing(true);

        try {
            const formData = new FormData();
            formData.append('image', file);

            const response = await fetch(getApiUrl('/api/albarans/scan'), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('artisan_token')}`
                },
                body: formData
            });

            if (!response.ok) {
                throw new Error('Failed to scan delivery note');
            }

            const data = await response.json();
            setAlbaranData(data.albaran);
            setEditableItems(data.albaran.items.map((item: AlbaranItem) => ({ ...item })));
            toast.success('Delivery note scanned successfully!');
        } catch (error) {
            console.error('Scan error:', error);
            toast.error('Failed to scan delivery note. Please try again.');
            setImage(null);
        } finally {
            setIsProcessing(false);
        }
    };

    const updateItem = (index: number, field: keyof AlbaranItem, value: string | number) => {
        setEditableItems(items =>
            items.map((item, i) =>
                i === index ? { ...item, [field]: value } : item
            )
        );
    };

    const confirmAlbaran = async () => {
        if (!albaranData) return;

        try {
            const itemsToAdd = editableItems.map((item) => ({
                code: item.code || `DEL-${Date.now().toString(36).toUpperCase()}`,
                name: item.name,
                quantity: item.quantity,
                unit: item.unit,
                unitCost: item.unitCost,
                totalCost: item.totalCost,
                currency: item.currency,
                supplier: albaranData.supplier?.name
            }));

            const apiHeaders = await getApiHeaders();
            const response = await fetch(getApiUrl('/api/inventory/items'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...apiHeaders
                },
                body: JSON.stringify({
                    items: itemsToAdd,
                    source: 'delivery'
                })
            });

            if (!response.ok) {
                throw new Error('Failed to save to inventory');
            }

            const data = await response.json();
            toast.success(`${data.itemsAdded} items added to inventory!`);
            setImage(null);
            setAlbaranData(null);
            setEditableItems([]);
        } catch (error) {
            console.error('Failed to update inventory:', error);
            toast.error('Failed to update inventory');
        }
    };

    const reset = () => {
        if (image) URL.revokeObjectURL(image);
        setImage(null);
        setAlbaranData(null);
        setEditableItems([]);
    };

    return (
        <div className="albaran-page animate-fade-in">
            <h1 className="page-title">
                <span className="title-icon">📄</span>
                Scan Delivery Note
            </h1>
            <p className="page-subtitle">
                Photograph your delivery note to automatically update inventory
            </p>

            {!image && !albaranData && (
                <div className="scan-section animate-slide-up">
                    <div
                        className="upload-zone"
                        onClick={() => cameraInputRef.current?.click()}
                        onDragOver={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        onDrop={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const file = e.dataTransfer.files?.[0];
                            if (file) {
                                const preview = URL.createObjectURL(file);
                                setImage(preview);
                                processAlbaran(file);
                            }
                        }}
                    >
                        <span className="upload-icon">📄</span>
                        <h3>Scan Delivery Note</h3>
                        <p>Take a photo or Drag & Drop your delivery note here</p>
                    </div>

                    <div className="camera-buttons">
                        <button
                            className="btn btn-primary btn-large"
                            onClick={() => cameraInputRef.current?.click()}
                        >
                            <span>📷</span>
                            Take Photo
                        </button>
                        <button
                            className="btn btn-secondary btn-large"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <span>🖼️</span>
                            Upload
                        </button>
                    </div>

                    <input
                        ref={cameraInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                    />
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                    />

                    <div className="tips-section">
                        <h3>📝 Tips for Best Results</h3>
                        <ul>
                            <li>Ensure good lighting</li>
                            <li>Keep the document flat</li>
                            <li>Include all text in the frame</li>
                            <li>Works with handwritten notes too!</li>
                        </ul>
                    </div>
                </div>
            )}

            {isProcessing && (
                <div className="processing-section animate-fade-in">
                    <div className="processing-animation">
                        <div className="loader-ring"></div>
                        <span className="processing-icon">📄</span>
                    </div>
                    <h2>Scanning Document...</h2>
                    <p>Extracting product and supplier information</p>
                </div>
            )}

            {albaranData && !isProcessing && (
                <div className="result-section animate-slide-up">
                    {image && (
                        <div className="scanned-image">
                            <img src={image} alt="Scanned delivery note" />
                        </div>
                    )}

                    <div className="card extracted-data">
                        <div className="data-header">
                            <div>
                                <span className="doc-type">{albaranData.documentType}</span>
                                <h3 className="doc-number">{albaranData.documentNumber || 'No Document Number'}</h3>
                                {albaranData.date && <p className="doc-date">{albaranData.date}</p>}
                            </div>
                            <div className="confidence-badge">
                                <span className="confidence-value">{Math.round(albaranData.confidence * 100)}%</span>
                                <span className="confidence-label">Confidence</span>
                            </div>
                        </div>

                        {albaranData.supplier?.name && (
                            <div className="supplier-info">
                                <span className="supplier-label">Supplier</span>
                                <span className="supplier-name">{albaranData.supplier.name}</span>
                            </div>
                        )}

                        <div className="edit-instructions">
                            <span className="instruction-icon">✏️</span>
                            <p>
                                <strong>Review & Edit:</strong> Verify each item below. Edit the name or SKU to match your existing products,
                                or leave as-is to create new inventory items.
                            </p>
                        </div>

                        <div className="items-section">
                            <h4>Items ({editableItems.length})</h4>
                            <div className="items-list editable">
                                {editableItems.map((item, index) => (
                                    <div key={index} className="item-row editable-item">
                                        <div className="item-inputs">
                                            <div className="input-group">
                                                <label>Product Name</label>
                                                <input
                                                    type="text"
                                                    value={item.name}
                                                    onChange={(e) => updateItem(index, 'name', e.target.value)}
                                                    placeholder="Enter product name"
                                                />
                                            </div>
                                            <div className="input-group small">
                                                <label>SKU Code</label>
                                                <input
                                                    type="text"
                                                    value={item.code}
                                                    onChange={(e) => updateItem(index, 'code', e.target.value)}
                                                    placeholder="SKU-001"
                                                />
                                            </div>
                                        </div>
                                        <div className="item-details-row">
                                            <div className="detail-group">
                                                <label>Qty</label>
                                                <input
                                                    type="number"
                                                    value={item.quantity}
                                                    onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 0)}
                                                    min="0"
                                                />
                                            </div>
                                            <div className="detail-group">
                                                <label>Unit</label>
                                                <input
                                                    type="text"
                                                    value={item.unit}
                                                    onChange={(e) => updateItem(index, 'unit', e.target.value)}
                                                />
                                            </div>
                                            {item.unitCost !== null && (
                                                <div className="detail-group">
                                                    <label>Cost</label>
                                                    <span className="cost-display">
                                                        {item.currency} {item.unitCost?.toFixed(2)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {albaranData.totals && (
                            <div className="totals-section">
                                <div className="total-row">
                                    <span>Total</span>
                                    <span className="total-value">
                                        {albaranData.totals.currency} {albaranData.totals.total.toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="action-buttons">
                        <button className="btn btn-secondary" onClick={reset}>
                            ← Scan Another
                        </button>
                        <button className="btn btn-primary" onClick={confirmAlbaran}>
                            ✓ Confirm & Update Stock
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AlbaranScanPage;
